import { Alert, Collapse, IconButton } from "@mui/material";
import { PropsWithChildren, ReactElement, useContext } from "react";
import { StorageContext } from "~/src/contexts/StorageContext/StorageProvider";
import { useStateful } from "~/src/hooks/useStateful";
import {
  CloseIcon,
  CompleteIcon,
  ErrorIcon,
  InfoIconLarger,
  SyncIcon,
  WarningIcon,
} from "../Icons/Icons";

export enum AlertVariant {
  Info,
  Error,
  Warning,
  CallToAction,
  Sync,
  SyncError,
  Success,
}

type AlertDefaultProps = PropsWithChildren & {
  variant?: AlertVariant;
  canClose?: boolean;
  id?: string;
  className?: string;
  rounded?: boolean;
  onClose?: () => void;
  bottomElement?: ReactElement;
};

export type AlertState = {
  show: true;
};

export default function AlertDefault({
  children,
  canClose,
  id,
  variant = AlertVariant.Info,
  className = "",
  rounded,
  onClose,
  bottomElement,
}: AlertDefaultProps): JSX.Element {
  const storageKey = { name: "AlertDefault", id: id };
  const storage = useContext(StorageContext);
  let state: AlertState = { show: true };
  if (id) {
    state = storage?.get<AlertState>(storageKey)?.data || state;
  }
  const isOpen = useStateful<boolean>(state.show);
  const handleClose = () => {
    isOpen.set(false);
    if (id) {
      storage?.add({ objectKey: storageKey, data: { show: false } });
    }
    onClose && onClose();
  };

  const severity =
    variant === AlertVariant.Error
      ? "error"
      : variant === AlertVariant.Warning
        ? "warning"
        : "info";

  let color;
  switch (variant) {
    case AlertVariant.Sync:
    case AlertVariant.Info:
      color = "bg-ldir-blue-500";
      break;
    case AlertVariant.Warning:
      color = "bg-ldir-orange-700";
      break;
    case AlertVariant.CallToAction:
      color = "bg-[#FFCE86]";
      break;
    case AlertVariant.Error:
    case AlertVariant.SyncError:
      color = "bg-ldir-red-700";
      break;

    case AlertVariant.Success:
      color = "bg-ldir-green-700";
  }

  return (
    <Collapse in={isOpen.value}>
      {variant === AlertVariant.Info || variant === AlertVariant.Success ? (
        <div
          className={`${
            rounded ? "rounded" : "rounded-none"
          } flex items-start justify-between p-4 text-ldir-brown-900 text-[1rem] ${color} ${className}`}
        >
          <div className="items-start justify-start flex grow">
            {variant === AlertVariant.Info ? (
              <InfoIconLarger className="mr-3" />
            ) : (
              <CompleteIcon className="mr-3" />
            )}
            <div className="grow">
              {children}
              {bottomElement && <div className="mt-6">{bottomElement}</div>}
            </div>
          </div>
          {canClose && (
            <IconButton title="Lukk" onClick={() => handleClose()}>
              <CloseIcon />
            </IconButton>
          )}
        </div>
      ) : (
        <div
          className={`${
            rounded ? "rounded" : "rounded-none"
          } py-2 px-4 text-ldir-brown-900 text-[1rem] ${color} ${className}`}
        >
          <Alert
            severity={severity}
            icon={
              <div className={` h-full flex items-start`}>
                {variant === AlertVariant.Warning ? (
                  <WarningIcon />
                ) : variant === AlertVariant.Sync ? (
                  <SyncIcon className="text-ldir-blue-900" />
                ) : variant === AlertVariant.CallToAction ? (
                  <svg
                    role="graphics-symbol"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M11 5L9 5L9 11L11 11L11 5ZM10 13C9.71667 13 9.47917 13.0958 9.2875 13.2875C9.09583 13.4792 9 13.7167 9 14C9 14.2833 9.09583 14.5208 9.2875 14.7125C9.47917 14.9042 9.71667 15 10 15C10.2833 15 10.5208 14.9042 10.7125 14.7125C10.9042 14.5208 11 14.2833 11 14C11 13.7167 10.9042 13.4792 10.7125 13.2875C10.5208 13.0958 10.2833 13 10 13ZM10 -8.74228e-07C11.3833 -7.53293e-07 12.6833 0.262498 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.787501 13.9C0.262499 12.6833 7.53293e-07 11.3833 8.74228e-07 10C9.95163e-07 8.61667 0.2625 7.31666 0.787502 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.787499C7.31667 0.262498 8.61667 -9.95163e-07 10 -8.74228e-07ZM10 2C7.76667 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.76666 2 10C2 12.2333 2.775 14.125 4.325 15.675C5.875 17.225 7.76667 18 10 18C12.2333 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.2333 18 10C18 7.76667 17.225 5.875 15.675 4.325C14.125 2.775 12.2333 2 10 2Z"
                      fill="#4E3629"
                    />
                  </svg>
                ) : (
                  <ErrorIcon sizeClassName="h-5 w-6" />
                )}
              </div>
            }
            className={`${color} text-[1rem] p-0 text-ldir-brown-900 font-sans`}
            action={
              canClose && (
                <IconButton
                  aria-label="lukk"
                  color="inherit"
                  onClick={canClose ? handleClose : undefined}
                >
                  <CloseIcon />
                </IconButton>
              )
            }
          >
            {children}
          </Alert>
          {bottomElement && <div className="mx-12 mb-4">{bottomElement}</div>}
        </div>
      )}
    </Collapse>
  );
}
